import { IconProps } from '@/components/common/Icon';
import { ORG_IDS, Routes } from '@/constants';
import { Organization } from '@/server/services/Auth0/Auth0.types';
import { PageHref, PageTitleMode, getPageTitle } from './routesMetadata';

export type NavigationRoute = {
  label: string;
  iconName: IconProps['name'];
  href: PageHref;
  isActiveLink?: (pathname: PageHref) => boolean;
  disabled?: boolean;
  hidden?: boolean | ((pathname: PageHref) => boolean);
  isBeta?: boolean;
  subRoutes?: NavigationRoute[];
};

const ORG_LABEL_OVERRIDES: Record<Organization['id'], Partial<Record<PageHref, string>>> = {
  [ORG_IDS.ELECTRO_MECHANICAL_SYSTEM_INC]: {
    [Routes.NARRATIVE_INTELLIGENCE]: 'Supply Chain Mapping',
  },
  [ORG_IDS.ZF_LIFETEC]: {
    [Routes.GEOPOLITICAL_INTELLIGENCE_INCIDENTS]: 'Supply Chain Geo Mapping',
  },
  [ORG_IDS.ZF_LIFETEC_DEMO]: {
    [Routes.GEOPOLITICAL_INTELLIGENCE_INCIDENTS]: 'Supply Chain Geo Mapping',
  },
  [ORG_IDS.DM_AUTO]: {
    [Routes.GEOPOLITICAL_INTELLIGENCE_INCIDENTS]: 'Supply Chain Geo Mapping',
  },
};

export const getNavigationRoutes = (orgId?: string): NavigationRoute[] => {
  const overrides = orgId ? ORG_LABEL_OVERRIDES[orgId] : undefined;
  if (!overrides) return navigationRoutes;

  const applyOverrides = (route: NavigationRoute): NavigationRoute => ({
    ...route,
    label: overrides[route.href] ?? route.label,
    ...(route.subRoutes && { subRoutes: route.subRoutes.map(applyOverrides) }),
  });

  return navigationRoutes.map(applyOverrides);
};

export const getOrgLabelOverride = (orgId: string | undefined, page: PageHref) =>
  orgId ? ORG_LABEL_OVERRIDES[orgId]?.[page] : undefined;

export const isSubRoute = (page: PageHref) => page.match(/\//g)!.length > 1;

export const invokeParentRoute = (page: PageHref) => {
  return isSubRoute(page) ? (page.split('/').slice(0, -1).join('/') as PageHref) : page;
};

export const navigationRoutes: NavigationRoute[] = [
  // Home
  {
    label: getPageTitle(Routes.DASHBOARD),
    iconName: 'DashboardIcon',
    href: Routes.DASHBOARD,
  },

  // Settings
  {
    label: getPageTitle(Routes.SETTINGS),
    iconName: 'SettingsIcon',
    href: Routes.SETTINGS,
    hidden: (pathname: PageHref) => pathname !== Routes.SETTINGS,
    subRoutes: [
      {
        label: getPageTitle(Routes.SETTINGS_ACCOUNT, { mode: PageTitleMode.SUB_ROUTE }),
        iconName: 'UserIcon',
        href: Routes.SETTINGS_ACCOUNT,
      },
      {
        label: getPageTitle(Routes.SETTINGS_SUBSCRIPTION, { mode: PageTitleMode.SUB_ROUTE }),
        iconName: 'CreditCardIcon',
        href: Routes.SETTINGS_SUBSCRIPTION,
      },
      {
        label: getPageTitle(Routes.SETTINGS_CACHE, { mode: PageTitleMode.SUB_ROUTE }),
        iconName: 'DatabaseIcon',
        href: Routes.SETTINGS_CACHE,
      },
    ],
  },

  // Narrative Intelligence
  {
    label: getPageTitle(Routes.NARRATIVE_INTELLIGENCE),
    iconName: 'NarrativeIcon',
    href: Routes.NARRATIVE_INTELLIGENCE,
    subRoutes: [
      // {
      //   label: getPageTitle(Routes.NARRATIVE_INTELLIGENCE_NARRATIVES),
      //   iconName: 'ChartBorderedIcon',
      //   href: Routes.NARRATIVE_INTELLIGENCE_NARRATIVES,
      //   disabled: true,
      // },
      {
        label: getPageTitle(Routes.NARRATIVE_INTELLIGENCE_NETWORKS),
        iconName: 'NetworkIcon',
        href: Routes.NARRATIVE_INTELLIGENCE_NETWORKS,
      },
    ],
  },

  // Supply Chain Mapping
  {
    label: getPageTitle(Routes.SUPPLY_CHAIN),
    iconName: 'SourcesIcon',
    href: Routes.EMBEDDED_SUPPLY_CHAIN,
    subRoutes: [
      {
        label: getPageTitle(Routes.EMBEDDED_SUPPLY_CHAIN, { mode: PageTitleMode.SUB_ROUTE }),
        iconName: 'DataGraphIcon',
        href: Routes.EMBEDDED_SUPPLY_CHAIN,
      },
    ],
  },

  // Intelligence Dashboard
  {
    label: getPageTitle(Routes.EMBEDDED_INTELLIGENCE_DASHBOARD),
    iconName: 'ChartBorderedIcon',
    href: Routes.EMBEDDED_INTELLIGENCE_DASHBOARD,
    subRoutes: [
      {
        label: getPageTitle(Routes.EMBEDDED_INTELLIGENCE_DASHBOARD, { mode: PageTitleMode.SUB_ROUTE }),
        iconName: 'DashboardIcon',
        href: Routes.EMBEDDED_INTELLIGENCE_DASHBOARD,
      },
    ],
  },

  // Incident Monitoring
  {
    label: getPageTitle(Routes.INCIDENTS),
    iconName: 'MapIcon',
    href: Routes.INCIDENTS,
    isActiveLink: (pathname: string) => ([Routes.INCIDENTS, Routes.INDEX] as PageHref[]).includes(pathname as PageHref),
  },

  // Geopolitical Intelligence
  {
    label: getPageTitle(Routes.GEOPOLITICAL_INTELLIGENCE),
    iconName: 'GeoPolitics',
    href: Routes.GEOPOLITICAL_INTELLIGENCE,
    subRoutes: [
      {
        label: getPageTitle(Routes.GEOPOLITICAL_INTELLIGENCE_INCIDENTS),
        iconName: 'MapIcon',
        href: Routes.GEOPOLITICAL_INTELLIGENCE_INCIDENTS,
        isActiveLink: (pathname) =>
          ([Routes.GEOPOLITICAL_INTELLIGENCE, Routes.GEOPOLITICAL_INTELLIGENCE_INCIDENTS] as PageHref[]).includes(
            pathname as PageHref,
          ),
      },
      {
        label: getPageTitle(Routes.GEOPOLITICAL_INTELLIGENCE_TRADE),
        iconName: 'TradeIcon',
        href: Routes.GEOPOLITICAL_INTELLIGENCE_TRADE,
        isActiveLink: (pathname) =>
          ([Routes.GEOPOLITICAL_INTELLIGENCE, Routes.GEOPOLITICAL_INTELLIGENCE_TRADE] as PageHref[]).includes(
            pathname as PageHref,
          ),
      },
      {
        label: getPageTitle(Routes.GEOPOLITICAL_INTELLIGENCE_ACTIONS),
        iconName: 'TaskListIcon',
        href: Routes.GEOPOLITICAL_INTELLIGENCE_ACTIONS,
        isActiveLink: (pathname) =>
          ([Routes.GEOPOLITICAL_INTELLIGENCE, Routes.GEOPOLITICAL_INTELLIGENCE_ACTIONS] as PageHref[]).includes(
            pathname as PageHref,
          ),
      },
    ],
  },

  // Reports
  {
    label: getPageTitle(Routes.REPORTS),
    iconName: 'ReportIcon',
    href: Routes.REPORTS,
    subRoutes: [
      {
        label: getPageTitle(Routes.REPORTS_CATALOG, { mode: PageTitleMode.SUB_ROUTE }),
        iconName: 'ReportIcon',
        /** TODO: Use "Routes.REPORTS_CATALOG" after add page */
        href: Routes.REPORTS_CATALOG,
        isActiveLink: (pathname) =>
          ([Routes.REPORTS, Routes.REPORTS_CATALOG] as PageHref[]).includes(pathname as PageHref),
      },
      {
        label: getPageTitle(Routes.REPORTS_COPILOT, { mode: PageTitleMode.SUB_ROUTE }),
        iconName: 'AiIcon',
        disabled: true,
        hidden: true,
        href: Routes.REPORTS_COPILOT,
      },
    ],
  },

  // Media
  {
    label: getPageTitle(Routes.SEARCH_CONSOLE),
    iconName: 'SearchIcon',
    href: Routes.SEARCH_CONSOLE,
  },
];
